import React, { useState } from 'react';
import { saveTrade } from '../../services/storage';
import { THEME } from '../../styles/theme';

// Zerodha Kite Connect requires TOTP-based OAuth — not embeddable in a React app without a backend.
// This component guides the user through manual API token flow + shows how to auto-import via
// the Kite Connect orderbook/tradebook endpoints. Angel One SmartAPI is similar.

const BROKERS = [
  {
    id:       'zerodha',
    name:     'Zerodha Kite',
    logo:     '🟢',
    color:    '#387ed1',
    apiUrl:   'https://api.kite.trade',
    docsUrl:  'https://kite.trade/docs/connect/v3/',
    steps: [
      'Go to kite.trade/connect → Create App → get api_key + api_secret',
      'Add http://localhost:3000/broker-callback as Redirect URL',
      'Paste your api_key and api_secret below',
      'Click Connect → Login with Zerodha → Copy the request_token from redirect URL',
      'Paste request_token below → JARVIS will generate access_token',
    ],
    fields: ['api_key', 'api_secret', 'request_token'],
    supports: ['tradebook', 'orderbook', 'positions', 'holdings'],
  },
  {
    id:       'angel',
    name:     'Angel One SmartAPI',
    logo:     '😇',
    color:    '#e64b3e',
    apiUrl:   'https://apiconnect.angelbroking.com',
    docsUrl:  'https://smartapi.angelbroking.com/docs',
    steps: [
      'Go to smartapi.angelbroking.com → Register App → get api_key',
      'Paste your Angel One client_id, password, and api_key below',
      'Enter your TOTP (Google Authenticator code)',
      'Click Connect → JARVIS will fetch auth token',
      'Your trades will be imported automatically',
    ],
    fields: ['api_key', 'client_id', 'password', 'totp'],
    supports: ['tradebook', 'orderbook', 'positions'],
  },
  {
    id:       'manual',
    name:     'Manual CSV Upload',
    logo:     '📁',
    color:    '#10b981',
    steps:    [],
    fields:   [],
    supports: ['tradebook'],
    note:     'Download tradebook CSV from your broker website and use Trade Import (MT4/MT5 tab is for forex, but the CSV importer also handles most Indian broker formats)',
  },
];

// Zerodha tradebook row → JARVIS trade
function mapZerodhaTrade(row) {
  const direction = (row.trade_type || row.transaction_type || '').toUpperCase().includes('BUY') ? 'BUY' : 'SELL';
  const price     = parseFloat(row.average_price || row.price || 0);
  const qty       = parseInt(row.quantity  || row.qty || 0, 10);
  const pnl       = parseFloat(row.pnl || row.realized_pnl || 0);

  return {
    id:          `BRKR${Date.now()}_${Math.random().toString(36).slice(2,5)}`,
    date:        (row.order_timestamp || row.trade_date || new Date().toISOString()).split('T')[0],
    symbol:      row.tradingsymbol || row.symbol || 'UNKNOWN',
    direction,
    entry:       direction === 'BUY'  ? price : null,
    exit:        direction === 'SELL' ? price : null,
    lot_size:    qty,
    result:      pnl > 0 ? 'WIN' : pnl < 0 ? 'LOSS' : 'OPEN',
    pnl_usd:     pnl,
    setup_type:  'BROKER_IMPORT',
    notes:       `Auto-imported from Zerodha. Order ID: ${row.order_id || '—'}`,
    source:      'Zerodha',
  };
}

const S = {
  wrap:       { padding: '20px', maxWidth: '900px', margin: '0 auto' },
  title:      { fontSize: '22px', fontWeight: '800', color: THEME.colors.blue, fontFamily: THEME.fonts.hud, letterSpacing: '2px', marginBottom: '6px' },
  sub:        { fontSize: '13px', color: 'rgba(255,255,255,0.4)', marginBottom: '24px' },

  brokerRow:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '14px', marginBottom: '28px' },
  brokerCard: (sel, col) => ({ background: sel ? `${col}11` : 'rgba(255,255,255,0.02)', border: `1px solid ${sel ? col : 'rgba(255,255,255,0.07)'}`, borderRadius: '12px', padding: '20px', cursor: 'pointer', transition: 'all 0.2s' }),
  brokerName: { fontSize: '15px', fontWeight: '700', color: '#e2e8f0', marginTop: '8px' },
  brokerSup:  { fontSize: '11px', color: 'rgba(255,255,255,0.35)', marginTop: '4px' },

  stepList:   { background: 'rgba(255,255,255,0.02)', borderRadius: '10px', padding: '20px', marginBottom: '20px' },
  stepTitle:  { fontSize: '11px', fontWeight: '700', letterSpacing: '1.5px', color: 'rgba(255,255,255,0.3)', marginBottom: '14px' },
  step:       { display: 'flex', gap: '12px', marginBottom: '10px', fontSize: '13px', color: '#9ca3af', lineHeight: '1.5' },
  stepNum:    { width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(0,212,255,0.15)', color: THEME.colors.blue, fontWeight: '700', fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' },

  fieldRow:   { marginBottom: '14px' },
  label:      { fontSize: '11px', color: 'rgba(255,255,255,0.4)', letterSpacing: '1px', marginBottom: '5px' },
  input:      { width: '100%', padding: '11px 14px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', fontSize: '14px', outline: 'none', boxSizing: 'border-box', fontFamily: THEME.fonts.body },

  btn:        (col=THEME.colors.blue, dis=false) => ({ padding: '11px 28px', borderRadius: '8px', border: 'none', background: dis ? '#374151' : col, color: dis ? '#6b7280' : col === THEME.colors.blue ? '#000' : '#fff', cursor: dis ? 'not-allowed' : 'pointer', fontWeight: '700', fontSize: '14px', marginRight: '10px' }),
  btnGhost:   { padding: '11px 20px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontSize: '13px' },

  notice:     (col='#f59e0b') => ({ background: `${col}11`, border: `1px solid ${col}33`, borderRadius: '8px', padding: '14px 16px', marginBottom: '16px', fontSize: '13px', color: col, lineHeight: '1.6' }),
  success:    { color: '#10b981', fontWeight: '700', fontSize: '14px', padding: '14px', background: '#10b98111', borderRadius: '8px', marginBottom: '12px' },
  error:      { color: '#ef4444', padding: '12px', background: '#ef444411', borderRadius: '8px', marginBottom: '12px', fontSize: '13px' },

  importedList:{ marginTop: '20px' },
  tradeRow:   (even) => ({ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto auto', gap: '10px', padding: '9px 12px', background: even ? 'rgba(255,255,255,0.02)' : 'transparent', borderRadius: '4px', fontSize: '13px', alignItems: 'center' }),
  tradeHead:  { display: 'grid', gridTemplateColumns: '1fr 1fr auto auto auto', gap: '10px', padding: '8px 12px', fontSize: '10px', fontWeight: '700', letterSpacing: '1px', color: 'rgba(255,255,255,0.3)' },
};

const FIELD_LABELS = { api_key: 'API Key', api_secret: 'API Secret', request_token: 'Request Token (after login)', client_id: 'Client ID', password: 'Password', totp: 'TOTP Code (Authenticator)' };
const FIELD_TYPES  = { password: 'password', totp: 'text', api_secret: 'password' };

export default function BrokerConnect() {
  const [broker,    setBroker]    = useState(null);
  const [fields,    setFields]    = useState({});
  const [status,    setStatus]    = useState('idle'); // idle | connecting | importing | done | error
  const [error,     setError]     = useState('');
  const [trades,    setTrades]    = useState([]);
  const [imported,  setImported]  = useState(0);

  const selected = BROKERS.find(b => b.id === broker);

  const setField = (k, v) => setFields(prev => ({ ...prev, [k]: v }));

  const connect = async () => {
    if (!selected) return;
    setStatus('connecting');
    setError('');

    if (broker === 'zerodha') {
      try {
        // Step 1: Get tradebook via Kite Connect (needs backend proxy since CORS blocks direct calls)
        // We call our own server which proxies to Kite API
        const res = await fetch('/api/broker/zerodha/tradebook', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify(fields),
        });
        if (res.status === 404) {
          // Backend endpoint not yet set up — show guide
          setError('Backend proxy for Zerodha not yet configured. See setup guide below.');
          setStatus('error');
          return;
        }
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Zerodha API error');
        const mapped = (data.data || []).map(mapZerodhaTrade).filter(Boolean);
        setTrades(mapped);
        setStatus('importing');
      } catch (e) {
        setError(e.message);
        setStatus('error');
      }
    } else if (broker === 'angel') {
      setError('Angel One SmartAPI requires server-side OAuth. See setup guide below.');
      setStatus('error');
    }
  };

  const importAll = async () => {
    let count = 0;
    for (const trade of trades) {
      saveTrade(trade);
      count++;
      if (count % 5 === 0) setImported(count);
      await new Promise(r => setTimeout(r, 5));
    }
    setImported(count);
    setStatus('done');
  };

  const reset = () => {
    setBroker(null);
    setFields({});
    setStatus('idle');
    setError('');
    setTrades([]);
    setImported(0);
  };

  return (
    <div style={S.wrap}>
      <div style={S.title}>BROKER CONNECT</div>
      <div style={S.sub}>
        Connect your broker API to auto-import all executed trades. JARVIS learns from 100% real trade data — no manual entry needed.
      </div>

      {/* Broker selector */}
      <div style={S.brokerRow}>
        {BROKERS.map(b => (
          <div key={b.id} style={S.brokerCard(broker === b.id, b.color || THEME.colors.blue)}
            onClick={() => { setBroker(b.id); setFields({}); setStatus('idle'); setError(''); setTrades([]); }}>
            <div style={{ fontSize: '28px' }}>{b.logo}</div>
            <div style={S.brokerName}>{b.name}</div>
            <div style={S.brokerSup}>{b.supports?.join(' · ')}</div>
          </div>
        ))}
      </div>

      {selected && (
        <>
          {/* Security notice */}
          <div style={S.notice('#f59e0b')}>
            ⚠️ <b>API keys are stored only in your browser localStorage — never sent to any external server except your chosen broker's API.</b>
            Never share your api_secret or password with anyone.
          </div>

          {selected.id === 'manual' ? (
            <div style={S.notice('#10b981')}>
              {selected.note}
              <br/><br/>
              <b>For Indian broker CSV exports:</b><br/>
              Zerodha: Console → Reports → Tradebook → Download CSV<br/>
              Angel One: Reports → Trade Book → Export<br/>
              HDFC Securities: Reports → Trading Report → Export<br/>
              Upstox: Reports → Tradebook → Export CSV<br/><br/>
              Use the <b>Trade Import</b> screen to upload the downloaded CSV.
            </div>
          ) : (
            <>
              {/* Steps */}
              <div style={S.stepList}>
                <div style={S.stepTitle}>SETUP STEPS — {selected.name.toUpperCase()}</div>
                {selected.steps.map((step, i) => (
                  <div key={i} style={S.step}>
                    <div style={S.stepNum}>{i + 1}</div>
                    <div>{step}</div>
                  </div>
                ))}
              </div>

              {/* API Fields */}
              {status === 'idle' && (
                <>
                  {selected.fields.map(f => (
                    <div key={f} style={S.fieldRow}>
                      <div style={S.label}>{FIELD_LABELS[f] || f.toUpperCase()}</div>
                      <input
                        style={S.input}
                        type={FIELD_TYPES[f] || 'text'}
                        placeholder={`Enter ${FIELD_LABELS[f] || f}`}
                        value={fields[f] || ''}
                        onChange={e => setField(f, e.target.value)}
                      />
                    </div>
                  ))}

                  <div style={{ marginTop: '16px' }}>
                    <button style={S.btn(selected.color || THEME.colors.blue, !selected.fields.every(f => fields[f]))}
                      onClick={connect}
                      disabled={!selected.fields.every(f => fields[f])}
                    >
                      Connect {selected.name}
                    </button>
                    <button style={S.btnGhost} onClick={() => setStatus('idle')}>Clear</button>
                  </div>
                </>
              )}

              {status === 'connecting' && (
                <div style={{ color: THEME.colors.textMuted, padding: '20px 0', fontSize: '14px' }}>
                  Connecting to {selected.name}...
                </div>
              )}

              {error && (
                <>
                  <div style={S.error}>{error}</div>
                  {broker === 'zerodha' && (
                    <div style={S.notice('#6366f1')}>
                      <b>🔧 Server-side setup required for Zerodha:</b><br/>
                      Add this to your <code style={{ color: '#e2e8f0' }}>server/index.js</code>:<br/><br/>
                      <code style={{ color: '#e2e8f0', display: 'block', background: 'rgba(0,0,0,0.3)', padding: '10px', borderRadius: '6px', marginTop: '8px', fontSize: '12px', whiteSpace: 'pre' }}>
{`const KiteConnect = require('kiteconnect').KiteConnect;
// npm install kiteconnect

app.post('/api/broker/zerodha/tradebook', async (req, res) => {
  const { api_key, api_secret, request_token } = req.body;
  const kite = new KiteConnect({ api_key });
  const session = await kite.generateSession(request_token, api_secret);
  kite.setAccessToken(session.access_token);
  const trades = await kite.getTrades();
  res.json({ data: trades });
});`}
                      </code>
                      Then run: <code style={{ color: '#e2e8f0' }}>npm install kiteconnect</code> in the server folder.
                    </div>
                  )}
                  <button style={{ ...S.btnGhost, marginTop: '10px' }} onClick={() => { setStatus('idle'); setError(''); }}>
                    Try Again
                  </button>
                </>
              )}
            </>
          )}
        </>
      )}

      {/* Trades preview before import */}
      {status === 'importing' && trades.length > 0 && (
        <>
          <div style={S.success}>
            ✅ Connected! Found {trades.length} trades from {selected?.name}.
          </div>

          <div style={S.importedList}>
            <div style={S.tradeHead}>
              <span>DATE</span><span>SYMBOL</span><span>DIR</span><span>P&L</span><span>RESULT</span>
            </div>
            {trades.slice(0, 30).map((t, i) => (
              <div key={t.id} style={S.tradeRow(i % 2 === 0)}>
                <span>{t.date}</span>
                <span>{t.symbol}</span>
                <span style={{ color: t.direction === 'BUY' ? '#10b981' : '#ef4444', fontWeight: '700' }}>{t.direction}</span>
                <span style={{ color: t.pnl_usd >= 0 ? '#10b981' : '#ef4444' }}>{t.pnl_usd >= 0 ? '+' : ''}{t.pnl_usd.toFixed(0)}</span>
                <span style={{ color: t.result === 'WIN' ? '#10b981' : t.result === 'LOSS' ? '#ef4444' : '#9ca3af' }}>{t.result}</span>
              </div>
            ))}
            {trades.length > 30 && (
              <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.3)', padding: '8px 12px' }}>
                +{trades.length - 30} more trades
              </div>
            )}
          </div>

          <div style={{ marginTop: '16px' }}>
            <button style={S.btn()} onClick={importAll}>
              Import All {trades.length} Trades to JARVIS
            </button>
            <button style={S.btnGhost} onClick={reset}>Cancel</button>
          </div>
        </>
      )}

      {status === 'done' && (
        <div style={S.success}>
          ✅ {imported} trades imported from {selected?.name}!
          JARVIS has learned from your complete trading history.
          Visit <b>Agent Network</b> to see updated strategy performance.
          <div style={{ marginTop: '12px' }}>
            <button style={S.btnGhost} onClick={reset}>Connect Another Broker</button>
          </div>
        </div>
      )}

      {/* Info section when nothing selected */}
      {!broker && (
        <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: '12px', padding: '24px', fontSize: '13px', color: 'rgba(255,255,255,0.5)', lineHeight: '1.9' }}>
          <div style={{ color: THEME.colors.blue, fontWeight: '700', marginBottom: '10px', fontSize: '13px' }}>WHY BROKER CONNECT?</div>
          Your real executed trades are the most accurate data JARVIS can learn from.<br/>
          No manual entry, no CSV formatting — every real trade you took auto-feeds the Agent Network.<br/><br/>
          <b style={{ color: '#e2e8f0' }}>Zerodha Kite Connect</b> — Full API, real-time positions, complete tradebook<br/>
          <b style={{ color: '#e2e8f0' }}>Angel One SmartAPI</b> — Tradebook, positions, real-time data<br/>
          <b style={{ color: '#e2e8f0' }}>Manual CSV</b> — Download from any broker, import via Trade Import screen
        </div>
      )}
    </div>
  );
}
